import type { ModelId } from '../geometry/models';
import type { ColourScheme, GridMode } from '../paint/codec';
import { MAX_BRUSH, MIN_BRUSH, type Role } from '../paint/motif';

export type Tool = 'brush' | 'eraser' | 'travel' | 'geodesic' | 'triangle' | 'circle';
export const MEASURE_TOOLS: readonly Tool[] = ['geodesic', 'triangle', 'circle'];
export const isMeasureTool = (t: Tool): t is 'geodesic' | 'triangle' | 'circle' =>
  MEASURE_TOOLS.includes(t);

export interface AppState {
  p: number;
  q: number;
  r: number;
  rotation: boolean;
  scheme: ColourScheme;
  model: ModelId;
  grid: GridMode;
  tool: Tool;
  role: Role;
  /** Brush slider position 0…100. */
  size: number;
  drift: boolean;
}

export const DEFAULT_STATE: AppState = {
  p: 7,
  q: 3,
  r: 2,
  rotation: true,
  scheme: 'single',
  model: 'disk',
  grid: 'off',
  tool: 'brush',
  role: 0,
  size: 38,
  drift: true,
};

/** Slider position → brush diameter as a fraction of the domain scale (quadratic feel). */
export const brushFromSlider = (v: number): number => {
  const t = Math.max(0, Math.min(100, v)) / 100;
  return MIN_BRUSH + (MAX_BRUSH - MIN_BRUSH) * t * t;
};

type Listener<T> = (state: T, prev: T) => void;

export class Store<T extends object> {
  private state: T;
  private listeners = new Set<Listener<T>>();

  constructor(initial: T) {
    this.state = initial;
  }

  get(): T {
    return this.state;
  }

  set(patch: Partial<T>): void {
    const prev = this.state;
    let changed = false;
    for (const k of Object.keys(patch) as (keyof T)[]) {
      if (patch[k] !== prev[k]) changed = true;
    }
    if (!changed) return;
    this.state = { ...prev, ...patch };
    for (const l of this.listeners) l(this.state, prev);
  }

  subscribe(l: Listener<T>): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }
}
